'use client';

import { useEffect, useRef, useState } from 'react';

export interface LivePollingOptions<T> {
  fetcher: () => Promise<T> | T;
  intervalMs?: number;
  immediate?: boolean;
  stop?: boolean;
}

export function useLivePolling<T>({
  fetcher,
  intervalMs = 5000,
  immediate = true,
  stop,
}: LivePollingOptions<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const fetcherRef = useRef(fetcher);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    if (stop) return;
    let cancelled = false;

    async function tick() {
      if (cancelled) return;
      setIsLoading(true);
      try {
        const result = await fetcherRef.current();
        if (cancelled) return;
        setData(result);
        setError(null);
        setLastUpdated(Date.now());
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Unbekannter Fehler'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    if (immediate) tick();
    const interval = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [intervalMs, immediate, stop]);

  function refresh() {
    (async () => {
      setIsLoading(true);
      try {
        const result = await fetcherRef.current();
        setData(result);
        setError(null);
        setLastUpdated(Date.now());
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unbekannt'));
      } finally {
        setIsLoading(false);
      }
    })();
  }

  return { data, error, isLoading, lastUpdated, refresh };
}

export function formatRelativeTime(timestamp: number | null): string {
  if (!timestamp) return 'nie';
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `vor ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `vor ${minutes} min`;
}
